/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect, useRef, useState } from 'react';

import { Button, Dialog, Link, StandaloneCheckbox, TextField, Typography2 } from '@coursera/cds-core';

import { accountDeleted } from 'bundles/account-settings/actions/SettingsActionCreators';
import { useNaptime } from 'bundles/naptimejs';

/* @ts-expect-error ts-migrate(7016) FIXME: Untyped import */
import UserAccountDelete from 'bundles/naptimejs/resources/userAccountDelete.v1';

import _t from 'i18n!nls/account-settings';

const loginDisclaimerID = 'deletion-disclaimer-login';

const anonymizedDisclaimerID = 'deletion-disclaimer-anonymized';

const certificatesDisclaimerID = 'deletion-disclaimer-certificates';

const styles = {
  checkbox: css`
    span.css-6erduj-Typography {
      font-size: 14px;
    }
  `,

  heading: css`
    margin-bottom: 25px;
    font-size: 32px;
  `,

  accountPasswordInput: css`
    width: 100%;
    font-size: 32px;
  `,

  deletionDisclaimer: css`
    margin: 30 0;
  `,

  disclaimerRow: css`
    margin-bottom: 10px;

    input[type='checkbox'] {
      margin: 0 10px 0 0;
      vertical-align: top;
    }

    label {
      font-weight: normal;
      margin: 0;
      margin-left: 10px;
      max-width: 600px;
    }
  `,
  deleteAction: css`
    margin-left: 10px;
    padding: 14px 15px;
  `,
  deleteActionCancel: css`
    padding: 14px 25px;
  `,
  failedErrorMessage: css`
    color: var(--cds-color-red-700);
  `,
  actionContainer: css`
    display: flex;
    justify-content: center;
    align-items: flex-end;
    flex-wrap: wrap;
  `,
};

type Props = {
  userId: number;
  onClose: () => void;
  onDelete?: () => void;
};

const AccountDeletionModal = ({ onClose, userId: id, onDelete }: Props) => {
  const passwordInputRef = useRef<HTMLInputElement | null>(null);
  const [isPasswordEmpty, setIsPasswordEmtpy] = useState(true);
  const [isAgreed, setIsAgreed] = useState(false);
  const [isInProgress, setIsInProgress] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [loginDisclaimer, setLoginDisclaimer] = useState(false);
  const [certificatesDisclaimer, setCertificateDisclaimer] = useState(false);
  const [anonymizedDisclaimer, setAnonymizedDisclaimer] = useState(false);
  const { naptime } = useNaptime(() => ({}));

  useEffect(() => {
    const agreed = loginDisclaimer && certificatesDisclaimer && anonymizedDisclaimer;
    setIsAgreed(agreed);
  }, [loginDisclaimer, certificatesDisclaimer, anonymizedDisclaimer]);

  useEffect(() => {
    if (isAgreed) {
      passwordInputRef.current?.focus();
    }
  }, [isAgreed]);

  const handleLoginDisclaimerChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const checked = evt.target.checked;
    setLoginDisclaimer(checked);
  };

  const handleAnonymizedDisclaimerChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const checked = evt.target.checked;
    setAnonymizedDisclaimer(checked);
  };

  const handleCertificatesDisclaimerChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const checked = evt.target.checked;
    setCertificateDisclaimer(checked);
  };

  const handlePasswordChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    setIsPasswordEmtpy(evt.target.value.length === 0);
  };

  const handleDeleteAccount = () => {
    setIsInProgress(true);
    const password = passwordInputRef.current?.value;

    const deleteUserAccount = UserAccountDelete.action(
      'refundAndDeleteAccount',
      {
        password,
      },
      { id }
    );

    naptime
      .executeMutation(deleteUserAccount)
      .then(() => {
        accountDeleted();
        if (onDelete) {
          onDelete();
        }
      })
      .catch((error) => {
        if (error.status === 429) {
          setErrorMessage(
            _t(
              'Account delete has been attempted recently with incorrect password. Please wait 5 minutes to attempt again.'
            )
          );
        } else {
          setErrorMessage(_t('Please re-enter your password and try again.'));
        }
        setIsInProgress(false);
      });
  };

  return (
    <Dialog className="rc-AccountDeletionModal" onClose={onClose} open>
      <Dialog.HeadingGroup>{_t('Deleting Your Account')}</Dialog.HeadingGroup>
      <Dialog.Content>
        <Typography2 component="p">
          {_t(
            `If you're sure you want to delete your Coursera account,

you'll need to enter your password and agree to the following:`
          )}
        </Typography2>

        <div css={[styles.checkbox, styles.deletionDisclaimer]}>
          <div css={styles.disclaimerRow}>
            <StandaloneCheckbox
              id={loginDisclaimerID}
              onChange={handleLoginDisclaimerChange}
              aria-label={_t('You will no longer be able to log into your account')}
              label={_t('You will no longer be able to log into your account')}
            />
          </div>

          <div css={styles.disclaimerRow}>
            <StandaloneCheckbox
              id={anonymizedDisclaimerID}
              onChange={handleAnonymizedDisclaimerChange}
              aria-label={_t(
                // eslint-disable-next-line no-restricted-syntax
                'Personally identifiable information in your profile will be removed. Discussion forum posts, peer review contributions, and other course activity will be anonymized but not deleted.'
              )}
              label={_t(
                // eslint-disable-next-line no-restricted-syntax
                'Personally identifiable information in your profile will be removed. Discussion forum posts, peer review contributions, and other course activity will be anonymized but not deleted.'
              )}
            />
          </div>

          <div css={styles.disclaimerRow}>
            <StandaloneCheckbox
              id={certificatesDisclaimerID}
              onChange={handleCertificatesDisclaimerChange}
              aria-label={_t(
                'Any certificates earned will be deleted. If you have posted your certificates on LinkedIn, the links back to them will no longer work.'
              )}
              label={_t(
                'Any certificates earned will be deleted. If you have posted your certificates on LinkedIn, the links back to them will no longer work.'
              )}
              supportText={<Link href="/accomplishments">{_t('Download your certificates (PDF)')}</Link>}
            />
          </div>
        </div>

        {errorMessage && <p css={styles.failedErrorMessage}>{errorMessage}</p>}

        <div css={styles.actionContainer}>
          <TextField
            id="deletion-password-input"
            type="password"
            placeholder={_t('Enter your password...')}
            inputRef={passwordInputRef}
            css={styles.accountPasswordInput}
            onChange={handlePasswordChange}
            disabled={!isAgreed || isInProgress}
            label={_t('Current Password')}
          />
        </div>
      </Dialog.Content>
      <Dialog.Actions>
        <Button id="cancel-bttn" css={styles.deleteActionCancel} onClick={onClose} type="button" variant="primary">
          {_t('Cancel')}
        </Button>

        <Button
          css={styles.deleteAction}
          disabled={isPasswordEmpty || !isAgreed || isInProgress}
          onClick={handleDeleteAccount}
          variant="secondary"
        >
          {isInProgress ? _t('Deleting Account') : _t('Delete Account')}
        </Button>
      </Dialog.Actions>
    </Dialog>
  );
};
export default AccountDeletionModal;
